import { Link } from 'react-router-dom';
import { Mail, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fleet-electrify-pro/artifacts/ko0dyyyw_WhatsApp_Image_2026-02-28_at_23.55.09-removebg-preview.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1a2744]" data-testid="main-footer">
      {/* Newsletter Section */}
      <div className="container-custom py-16">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
              Stay Informed on EV Infrastructure
            </h3>
            <p className="text-slate-400 text-lg">
              Get the latest industry updates and insights delivered to your inbox.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="px-6 py-4 bg-[#2a3a5c] border border-slate-600 rounded-lg text-white placeholder-slate-400 w-full sm:w-80 focus:outline-none focus:border-[#00A2B5]"
              data-testid="newsletter-email"
            />
            <button 
              className="px-8 py-4 bg-gradient-to-r from-[#00A2B5] to-[#36D890] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
              data-testid="newsletter-subscribe"
            >
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="container-custom">
        <div className="border-t border-slate-700"></div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          
          {/* Logo & Description Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8" data-testid="footer-logo">
              <img 
                src={LOGO_URL} 
                alt="Skytec EV" 
                className="h-24 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              Lead the Charge.
            </p>
            <p className="text-slate-400 leading-relaxed mb-10">
              Utility-grade design, construction, and maintenance for fleets, utilities, and commercial properties. A Skytec Company.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-5">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-6 h-6" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="footer-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-6 h-6" />
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Spacer */}
          <div className="hidden lg:block lg:col-span-1"></div>

          {/* Solutions Column */}
          <div className="lg:col-span-2">
            <h4 className="font-bold text-white text-lg uppercase tracking-wider mb-8">Solutions</h4>
            <ul className="space-y-5">
              <li>
                <Link to="/solutions#fleet" className="text-slate-400 hover:text-white transition-colors">
                  Fleet & Depot Charging
                </Link>
              </li>
              <li>
                <Link to="/solutions#commercial" className="text-slate-400 hover:text-white transition-colors">
                  Commercial & Retail
                </Link>
              </li>
              <li>
                <Link to="/solutions#municipal" className="text-slate-400 hover:text-white transition-colors">
                  Utilities & Municipalities
                </Link>
              </li>
              <li>
                <Link to="/solutions#caas" className="text-slate-400 hover:text-white transition-colors">
                  Charging-as-a-Service
                </Link>
              </li>
            </ul>
          </div>

          {/* Company Column */}
          <div className="lg:col-span-2">
            <h4 className="font-bold text-white text-lg uppercase tracking-wider mb-8">Company</h4>
            <ul className="space-y-5">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/projects" className="text-slate-400 hover:text-white transition-colors">
                  Projects
                </Link>
              </li>
              <li>
                <Link to="/technology" className="text-slate-400 hover:text-white transition-colors">
                  Technology
                </Link>
              </li>
              <li>
                <Link to="/careers" className="text-slate-400 hover:text-white transition-colors">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-slate-400 hover:text-white transition-colors">
                  Resources
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-white transition-colors">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-3">
            <h4 className="font-bold text-white text-lg uppercase tracking-wider mb-8">Contact</h4>
            <ul className="space-y-6">
              <li>
                <a 
                  href="mailto:info@SkytecEV.com" 
                  className="flex items-center gap-4 text-slate-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 text-[#00C5A5]" />
                  <span>info@SkytecEV.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-4 text-slate-400">
                  <MapPin className="w-5 h-5 text-[#00C5A5] mt-1 flex-shrink-0" />
                  <div>
                    <p>5092 SW 73rd Avenue</p>
                    <p>Davie, FL 33314</p>
                    <p>United States</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700">
        <div className="container-custom py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} Skytec EV. A Skytec Company. All rights reserved.
            </p>
            
            <div className="flex items-center gap-8 text-sm">
              <Link to="/privacy" className="text-slate-500 hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-500 hover:text-white transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
